import { API_URL } from "./env";
import { getAccessToken, getExpiresAt, getValidToken, refreshSession } from "./auth";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiOptions extends Omit<RequestInit, "body"> {
  /** แนบ Authorization (ค่าเริ่มต้น true) ถ้าไม่มี token จะส่งแบบไม่แนบ */
  auth?: boolean;
  json?: unknown;
  body?: BodyInit | null;
}

/** fetch ไป API พร้อมแนบ access token ถ้าได้ 401 จะ refresh แล้วลองใหม่ 1 ครั้ง */
export async function apiFetch(path: string, opts: ApiOptions = {}): Promise<Response> {
  const { auth = true, json, headers, ...rest } = opts;
  const send = async (token: string | null): Promise<Response> => {
    const h = new Headers(headers);
    if (token) h.set("Authorization", `Bearer ${token}`);
    let body = rest.body;
    if (json !== undefined) {
      h.set("Content-Type", "application/json");
      body = JSON.stringify(json);
    }
    return fetch(`${API_URL}${path}`, { ...rest, headers: h, body });
  };

  const token = auth ? await getValidToken() : null;
  let res = await send(token);
  if (res.status === 401 && auth) {
    const r = await refreshSession();
    if (r.status === "ok") res = await send(r.accessToken);
  }
  if (res.status === 403 && auth) await announceConsentRequired(res);
  return res;
}

/** ชื่อ event ที่ยิงเมื่อ server ตอบ 403 consent_required (ตัวรับอยู่ที่ ConsentRedirector) */
export const CONSENT_REQUIRED_EVENT = "wc:consent-required";

/** ถ้า 403 เป็น consent_required ให้แจ้งทั้งแอป เพื่อพาไปหน้ายินยอมจากทุกจุดที่เรียก API */
async function announceConsentRequired(res: Response): Promise<void> {
  try {
    const body = (await res.clone().json()) as { error?: string } | null;
    if (body?.error === "consent_required" && typeof window !== "undefined") window.dispatchEvent(new Event(CONSENT_REQUIRED_EVENT));
  } catch {
    /* ไม่ใช่ JSON */
  }
}

async function errorCode(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.clone().json()) as { error?: unknown } | null;
    return typeof body?.error === "string" ? body.error : undefined;
  } catch {
    return undefined;
  }
}

/**
 * ส่งคำขอแบบ keepalive ทันที (ไม่รอ refresh token) สำหรับตอนหน้ากำลังปิด (pagehide) ที่เบราว์เซอร์รอ async ไม่ได้
 * ใช้ได้เมื่อมี access token ที่ยังไม่หมดอายุในหน่วยความจำ คืน false ถ้าส่งไม่ได้ (ผู้เรียกต้องไม่ถือว่าส่งสำเร็จ)
 * ไม่มี body เพราะ keepalive จำกัดขนาด ใช้กับคำขอสั้นอย่าง DELETE
 */
export function apiKeepalive(path: string, method: "DELETE" | "POST"): boolean {
  const token = getAccessToken();
  if (!token || Date.now() >= getExpiresAt()) return false;
  try {
    void fetch(`${API_URL}${path}`, { method, keepalive: true, headers: { Authorization: `Bearer ${token}` } }).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

export async function apiJson<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const res = await apiFetch(path, opts);
  if (!res.ok) throw new ApiError(res.status, `${opts.method ?? "GET"} ${path} failed with ${res.status}`, await errorCode(res));
  return (await res.json()) as T;
}
